import React, {useState, useCallback} from "react";
import moment from "moment";
import {executeGet} from "../../Api";
import {amountFormatterWithZero, saveLogRocketSession} from "../../utils";
import "./payersTable.css";

const Settlements = (props) => {
    const {subdomain, themeColor1} = props;

    const [startDate, setStartDate] = useState(moment().subtract(7, "days").format("YYYY-MM-DD"));
    const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"));
    const [batches, setBatches] = useState([]);
    const [summary, setSummary] = useState(null);
    const [timezone, setTimezone] = useState("UTC");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [expandedBatchId, setExpandedBatchId] = useState(null);

    const fetchSettlements = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const url = `transaction/settlement/batches?subdomain=${subdomain}&startDate=${startDate}&endDate=${endDate}`;
            const result = await executeGet(url);
            setBatches(result.data?.batches || []);
            setSummary(result.data?.summary || null);
            setTimezone(result.data?.timezone || "UTC");
        } catch (err) {
            console.error("Error fetching settlements:", err);
            const msg = err.response?.data?.message || err.message || "Error fetching settlement data";
            setError(msg);
            setBatches([]);
            setSummary(null);
            saveLogRocketSession({eventType: "settlement_fetch_error", metadata: {error: msg}});
        } finally {
            setLoading(false);
        }
    }, [subdomain, startDate, endDate]);

    const handleDownloadCsv = () => {
        const baseUrl = process.env.REACT_APP_API;
        const url = `${baseUrl}transaction/settlement/csv?subdomain=${subdomain}&startDate=${startDate}&endDate=${endDate}`;
        window.open(url, "_blank");
    };

    const setPreset = (days) => {
        setStartDate(moment().subtract(days, "days").format("YYYY-MM-DD"));
        setEndDate(moment().format("YYYY-MM-DD"));
    };

    const fmt = (n) => amountFormatterWithZero(parseFloat(n) || 0);

    return (
        <div className="intake-content-admin">
            <div className="jumbotron p-4 p-lg-5">
                <h2 className="mb-4">Settlement Reports</h2>
                <p className="text-muted mb-4">
                    View batch settlement details for transactions deposited to your bank account.
                </p>

                {/* Date Range Controls */}
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="row align-items-end">
                            <div className="col-md-3 mb-2">
                                <label className="form-label fw-bold">Start Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                />
                            </div>
                            <div className="col-md-3 mb-2">
                                <label className="form-label fw-bold">End Date</label>
                                <input
                                    type="date"
                                    className="form-control"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                />
                            </div>
                            <div className="col-md-6 mb-2">
                                <div className="d-flex gap-2 flex-wrap">
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setPreset(7)}
                                    >
                                        Last 7 days
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setPreset(14)}
                                    >
                                        Last 14 days
                                    </button>
                                    <button
                                        className="btn btn-outline-secondary btn-sm"
                                        onClick={() => setPreset(30)}
                                    >
                                        Last 30 days
                                    </button>
                                    <button
                                        className="sos-custom-btn sos-custom-btn-css"
                                        onClick={fetchSettlements}
                                        disabled={loading}
                                    >
                                        {loading ? "Loading..." : "View Report"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {error && (
                    <div className="alert alert-danger">{error}</div>
                )}

                {/* Summary */}
                {summary && (
                    <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center"
                             style={{backgroundColor: themeColor1, color: "#fff"}}>
                            <h4 className="mb-0">Summary</h4>
                            {batches.length > 0 && (
                                <button
                                    className="btn btn-sm btn-light"
                                    onClick={handleDownloadCsv}
                                >
                                    Download CSV
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            <div className="row text-center">
                                <div className="col-md-3">
                                    <div className="border rounded p-3">
                                        <div className="text-muted small">Charges</div>
                                        <div className="fs-4 fw-bold">${fmt(summary.totalCharges)}</div>
                                        <div className="text-muted small">{summary.chargeCount} transactions</div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3">
                                        <div className="text-muted small">Refunds</div>
                                        <div className="fs-4 fw-bold text-danger">
                                            {summary.totalRefunds > 0 ? "-" : ""}${fmt(summary.totalRefunds)}
                                        </div>
                                        <div className="text-muted small">{summary.refundCount} transactions</div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3">
                                        <div className="text-muted small">Net Deposit</div>
                                        <div className="fs-4 fw-bold" style={{color: themeColor1}}>
                                            ${fmt(summary.netDeposit)}
                                        </div>
                                        <div className="text-muted small">deposited to bank</div>
                                    </div>
                                </div>
                                <div className="col-md-3">
                                    <div className="border rounded p-3">
                                        <div className="text-muted small">Batches</div>
                                        <div className="fs-4 fw-bold">{summary.batchCount}</div>
                                        <div className="text-muted small">{summary.transactionCount} total txns</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Batches Table */}
                {batches.length > 0 && (
                    <div className="card">
                        <div className="card-header">
                            <h4 className="mb-0">Settlement Batches</h4>
                        </div>
                        <div className="card-body p-0">
                            <table className="table table-bordered table-sm mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th style={{width: "30px"}}></th>
                                        <th>Settlement Date</th>
                                        <th>Batch ID</th>
                                        <th>Charges</th>
                                        <th>Charge Total</th>
                                        <th>Refunds</th>
                                        <th>Refund Total</th>
                                        <th>Net Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {batches.map((batch) => {
                                        const isExpanded = expandedBatchId === batch.batchId;
                                        const settleDateFormatted = batch.settleDate
                                            ? moment(batch.settleDate, "YYYYMMDDHHmmss").format("MM/DD/YYYY")
                                            : "N/A";

                                        return (
                                            <React.Fragment key={batch.batchId}>
                                                <tr
                                                    style={{cursor: "pointer"}}
                                                    onClick={() => setExpandedBatchId(isExpanded ? null : batch.batchId)}
                                                    className={isExpanded ? "table-active" : ""}
                                                >
                                                    <td className="text-center">
                                                        {isExpanded ? "\u25BC" : "\u25B6"}
                                                    </td>
                                                    <td>{settleDateFormatted}</td>
                                                    <td><strong>{batch.batchId}</strong></td>
                                                    <td>{batch.chargeCount}</td>
                                                    <td>${fmt(batch.chargeTotal)}</td>
                                                    <td>{batch.refundCount}</td>
                                                    <td>{batch.refundTotal > 0 ? "-" : ""}${fmt(batch.refundTotal)}</td>
                                                    <td><strong>${fmt(batch.netAmount)}</strong></td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="8" style={{padding: 0}}>
                                                            <div style={{
                                                                backgroundColor: "#f8f9fa",
                                                                padding: "12px",
                                                                maxHeight: "400px",
                                                                overflowY: "auto"
                                                            }}>
                                                                <table className="table table-sm table-bordered mb-0">
                                                                    <thead>
                                                                        <tr style={{backgroundColor: themeColor1, color: "#fff"}}>
                                                                            <th>Transaction ID</th>
                                                                            <th>Name</th>
                                                                            <th>Transaction Date</th>
                                                                            <th>Settlement Date</th>
                                                                            <th>Type</th>
                                                                            <th>Amount</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {batch.transactions.map((txn, idx) => {
                                                                            const saleDateFmt = txn.saleDate
                                                                                ? moment(txn.saleDate, "YYYYMMDDHHmmss").format("MM/DD/YY HH:mm")
                                                                                : "-";
                                                                            const settleDateFmt = txn.settleDate
                                                                                ? moment(txn.settleDate, "YYYYMMDDHHmmss").format("MM/DD/YY HH:mm")
                                                                                : "-";
                                                                            return (
                                                                                <tr key={idx}>
                                                                                    <td>{txn.transactionId}</td>
                                                                                    <td>{txn.firstName} {txn.lastName}</td>
                                                                                    <td>{saleDateFmt}</td>
                                                                                    <td>{settleDateFmt}</td>
                                                                                    <td>
                                                                                        <span className={`badge ${txn.isRefund ? "bg-danger" : "bg-success"}`}>
                                                                                            {txn.isRefund ? "Refund" : "Sale"}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td>
                                                                                        {txn.isRefund ? "-" : ""}${fmt(txn.amount)}
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Empty state */}
                {!loading && summary && batches.length === 0 && (
                    <div className="alert alert-info">
                        No settlement batches found for the selected date range.
                    </div>
                )}
            </div>
        </div>
    );
};

export default Settlements;
